import InventoryTerms from '../components/inventory/InventoryTerms';
// src/pages/ProductPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, Loader2 } from 'lucide-react';
import { getProduct, getProducts } from '../api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import ProductCard from '../components/ProductCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CartSidebar from '../components/CartSidebar';
import LoginModal from '../components/LoginModal';
import { Button } from '../components/ui/button';
import ZoomImage from '../components/ZoomImage';

const ProductPage = () => {
  const { article } = useParams();
  const productArticle = article;

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');

  const { addToCart, setIsCartOpen } = useCart();
  const { user, setIsLoginModalOpen } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    loadProduct();
  }, [productArticle]);

  const loadProduct = async () => {
    setLoading(true);
    setSelectedSize('');
    setCurrentImageIndex(0);
    try {
      const data = await getProduct(productArticle);
      setProduct(data);

      if (data.category) {
        const related = await getProducts(data.category);
        setRelatedProducts(related.filter(p => p.article !== productArticle).slice(0, 4));
      }
    } catch (error) {
      console.error('Failed to load product:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] flex items-center justify-center transition-colors">
        <Loader2 className="w-8 h-8 animate-spin text-brand-text" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
        <Header />
        <main className="pt-24 pb-24">
          <div className="text-center">
            <h1 className="text-2xl font-medium mb-4 text-brand-text dark:text-brand-text">Товар не найден</h1>
            <Link to="/" className="text-brand-text dark:text-brand-text hover:text-brand-text dark:hover:text-brand-text underline">
              Вернуться на главную
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const nextImage = () => {
    if (product.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Пожалуйста, выберите размер');
      return;
    }
    addToCart(product, selectedSize);
    setIsCartOpen(true);
  };

  const handleFavoriteClick = () => {
    if (!user) {
      setIsLoginModalOpen(true);
      return;
    }
    toggleFavorite(product.id);
  };

  const isLiked = isFavorite(product.id);

  const safeImageUrl = (url) => {
    if (!url) return '/placeholder.jpg';
    return url.startsWith('/') ? url : `/placeholder.jpg`;
  };

  return (
    <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
      <Header />
      <CartSidebar />
      <LoginModal />

      <main className="pt-20 md:pt-24 pb-24">
        <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
          {/* Breadcrumb */}
          <div className="py-4">
            <Link to="/" className="text-sm text-brand-text dark:text-brand-text hover:text-brand-text dark:hover:text-brand-text transition-colors">
              ← Назад к товарам
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Gallery */}
            <div className="space-y-4">
              <div className="relative w-full max-w-md mx-auto rounded-xl overflow-hidden">
                {product.images && product.images.length > 0 ? (
                  <>
                    <ZoomImage
                      src={safeImageUrl(product.images[currentImageIndex])}
                      alt={product.name}
                      className="w-full h-auto object-contain"
                      style={{ maxHeight: '70vh' }}
                    />
                    
                    {product.images.length > 1 && (
                      <>
                        {/* Стрелка "Назад" — без фона */}
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center 
                                     text-brand-text dark:text-brand-text hover:text-brand-text dark:hover:text-brand-text 
                                     transition-colors focus:outline-none"
                          aria-label="Предыдущее изображение"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        
                        {/* Стрелка "Вперёд" — без фона */}
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center 
                                     text-brand-text dark:text-brand-text hover:text-brand-text dark:hover:text-brand-text 
                                     transition-colors focus:outline-none"
                          aria-label="Следующее изображение"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full py-16 flex items-center justify-center text-brand-text">
                    Нет фото
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-20 h-24 flex-shrink-0 overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex 
                          ? 'border-black dark:border-white' 
                          : 'border-transparent'
                      }`}
                    >
                      <img
                        src={safeImageUrl(img)}
                        alt={`${product.name} ${idx + 1}`}
                        onError={(e) => {
                          e.target.src = '/placeholder.jpg';
                        }}
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="lg:sticky lg:top-28 lg:self-start space-y-6">
              <div>
                <p className="text-sm text-brand-text dark:text-brand-text mb-2">арт: {product.article}</p>
                <h1 className="text-2xl md:text-3xl font-medium text-brand-text dark:text-brand-text">{product.name}</h1>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-2xl font-semibold text-brand-text dark:text-brand-text">
                  {product.price?.toLocaleString('ru-RU')} ₽
                </span>
                {product.old_price && (
                  <span className="text-lg text-brand-text dark:text-brand-text line-through">
                    {product.old_price.toLocaleString('ru-RU')} ₽
                  </span>
                )}
              </div>

              {product.sizes && product.sizes.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-brand-text dark:text-brand-text">Размер</p>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`w-12 h-12 border-2 flex items-center justify-center font-medium transition-colors ${
                          selectedSize === size
                            ? 'border-black bg-brand-accent text-brand-text dark:border-white dark:bg-brand-accent dark:text-brand-text'
                            : 'border-brand-border dark:border-brand-border hover:border-brand-border dark:hover:border-brand-border'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedSize && <InventoryTerms items={[{id:product.id,size:selectedSize,quantity:1}]}/>}
              <div className="flex gap-3">
                <Button
                  onClick={handleAddToCart}
                  className="flex-1 h-14 bg-brand-surface text-brand-text hover:bg-brand-surface text-base font-medium border border-brand-border dark:border-white"
                >
                  Добавить в корзину
                </Button>
                <button
                  onClick={handleFavoriteClick}
                  className={`w-14 h-14 p-0 flex items-center justify-center transition-colors ${
                    isLiked
                      ? 'text-brand-text dark:text-brand-text'
                      : 'text-brand-text dark:text-brand-text'
                  }`}
                >
                  <Heart
                    className={`w-6 h-6 ${isLiked ? 'fill-current stroke-current' : 'stroke-current'}`}
                    strokeWidth={1.5}
                  />
                </button>
              </div>

              {product.description && (
                <div className="pt-6 border-t border-brand-border dark:border-brand-border">
                  <p className="text-brand-text dark:text-brand-text leading-relaxed whitespace-pre-line">
                    {product.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-16 pt-8 border-t border-brand-border dark:border-brand-border">
              <h2 className="text-xl font-medium mb-8 text-brand-text dark:text-brand-text">Смотрите также</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {relatedProducts.map(p => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductPage;